import React, { useRef, useState, useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import {
  Card,
  Container,
  Row,
  Button,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import helpers from "utils/helpers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useAuth } from "context/AuthContext";
import Pagination from "../components/Pagination";

function NotificationTemplateTable({
  templates,
  isLoading,
  tableState,
  setTableState,
  meta,
  onPreview,
  onToggleStatus,
  onDeleteClick
}) {
  const componentRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(tableState.search || "");
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { permission } = useAuth();
  const templatePermission = permission?.permissions?.modules?.notificationTemplate;

  useEffect(() => {
    const timer = setTimeout(() => {
      setTableState((prev) => ({
        ...prev,
        search: localSearch,
        page: 1,
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearch, setTableState]);

  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    setTableState((prev) => ({
      ...prev,
      page,
      limit: sizePerPage,
      sortBy: sortField || prev.sortBy,
      sortOrder: sortOrder ?? prev.sortOrder,
    }));
  };

  const columns = [
    {
      dataField: "name",
      text: t("table.headers.templateName", "Template Name"),
      sort: true,
      formatter: (cell) =>
        cell
          ?.replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
    },
    {
      dataField: "subject",
      text: t("table.headers.subject", "Subject"),
      sort: true,
      headerStyle: { minWidth: "250px" },
      style: { minWidth: "250px" },
      formatter: (cell) => (
        <div className="subject-cell">{cell}</div>
      ),
    },
    {
      dataField: "type",
      text: t("table.headers.type", "Type"),
      formatter: (cell) => (
        <Badge color={cell === "SYSTEM" ? "primary" : "info"}>
          {t(`page.notificationTemplates.type.${cell}`)}
        </Badge>
      ),
    },
    {
      dataField: "isActive",
      text: t("table.headers.status", "Status"),
      align: "left",
      headerAlign: "left",
      formatter: (cell, row) => {
        const currentStatus = cell ? "active" : "inactive";
        const statusClass = helpers.getStatusClass(
          currentStatus,
          "notificationTemplate"
        );

        const canUpdate = templatePermission?.update;

        return (
          <UncontrolledDropdown disabled={!canUpdate}>
            <DropdownToggle
              caret
              size="sm"
              key={`status-${row._id}-${language}`}
              className={`status-dropdown ${statusClass}`}
              disabled={!canUpdate}
            >
              {t(`common.${currentStatus}`)}
            </DropdownToggle>

            <DropdownMenu>
              {Object.keys(helpers.NOTIFICATION_TEMPLATE_STATUSES).map(
                (status) => (
                  <DropdownItem
                    key={status}
                    disabled={status === currentStatus || !canUpdate}
                    onClick={() =>
                      onToggleStatus(row, status === "active")
                    }
                  >
                    {t(`common.${status}`)}
                  </DropdownItem>
                )
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
    {
      dataField: "createdAt",
      text: t("table.headers.createdAt", "Created At"),
      sort: true,
      formatter: (cell) => (cell ? helpers.getFormattedDate(cell, t) : "-"),
    },
    {
      dataField: "actions",
      text: t("table.headers.actions", "Actions"),
      formatter: (_, row) => {
        const canRead = templatePermission?.read;
        const canUpdate = templatePermission?.update;
        const canDelete = templatePermission?.delete;

        if (!canRead && !canDelete && !canUpdate) {
          return (
            <div className="text-center text-muted w-100">N/A</div>
          );
        }

        return (
          <div className="d-flex align-items-center gap-2">
            <Button
              id={`preview-${row._id}`}
              size="sm"
              onClick={() => canRead && onPreview(row._id)}
              disabled={!canRead}
            >
              <VisibilityIcon fontSize="small" color={"primary"} />
            </Button>
            <UncontrolledTooltip target={`preview-${row._id}`}>
              {canRead
                ? t("page.notificationTemplates.buttons.preview", "Preview")
                : t("tooltip.viewNotAllowed", "View not allowed")}
            </UncontrolledTooltip>

            <Button
              id={`edit-${row._id}`}
              size="sm"
              disabled={row.isBaseTemplate || !canUpdate}
              onClick={() =>
                canUpdate && navigate(`/admin/notification-templates/edit/${row._id}`)
              }
            >
              <EditIcon fontSize="small" color={"info"} />
            </Button>
            <UncontrolledTooltip target={`edit-${row._id}`}>
              {row.isBaseTemplate
                ? t("page.notificationTemplates.tooltips.cannotEditBase", "Base templates cannot be edited")
                : !canUpdate
                ? t("tooltip.editNotAllowed", "You don't have permission to edit")
                : t("page.roles.actions.edit", "Edit")}
            </UncontrolledTooltip>

            <Button
              id={`delete-${row._id}`}
              size="sm"
              disabled={row.isBaseTemplate || !canDelete}
              onClick={() => canDelete && onDeleteClick(row)}
            >
              <DeleteIcon fontSize="small" color={"error"} />
            </Button>
            <UncontrolledTooltip target={`delete-${row._id}`}>
              {row.isBaseTemplate
                ? t("page.notificationTemplates.tooltips.cannotDeleteBase", "Base templates cannot be deleted")
                : !canDelete
                ? t("tooltip.deleteNotAllowed", "Delete not allowed")
                : t("page.roles.delete", "Delete")}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SimpleHeader
        name={t("sideNav.notificationTemplate", "Notification Templates")}
        parentName={t("page.notificationTemplates.subTitle", "Manage templates")}
      />

      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <div className="py-4 position-relative" ref={componentRef}>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center px-4 px-md-4 pb-3 gap-2">
                  <div className="d-flex align-items-center">
                    <span className="mr-2 whitespace-nowrap text-sm">
                      {t("table.search", "Search")}:
                    </span>

                    <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm w-100"
                        placeholder={t("page.notificationTemplates.searchPlaceholder", "Search by name")}
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                        style={{ paddingRight: "24px" }}
                      />
                      {localSearch && (
                        <span
                          onClick={() => {
                            setLocalSearch("");
                            setTableState((prev) => ({ ...prev, search: "", page: 1 }));
                          }}
                          className="position-absolute cursor-pointer search-clear-btn"
                        >
                          <CloseIcon style={{ fontSize: "14px" }} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    {templatePermission?.create && (
                      <Button
                        color="warning"
                        size="md"
                        onClick={() =>
                          navigate("/admin/notification-templates/create")
                        }
                        className="w-100 w-md-auto d-flex align-items-center justify-content-center"
                      >
                        <AddIcon fontSize="small" className="mr-1" />
                        {t("page.notificationTemplates.buttons.createTemplate", "Create Template")}
                      </Button>
                    )}
                  </div>
                </div>

                <ToolkitProvider key={language} keyField="_id" data={templates} columns={columns}>
                  {(props) => (
                    <>
                      <div className="table-responsive px-4">
                        <BootstrapTable
                          {...props.baseProps}
                          bootstrap4
                          bordered={false}
                          remote
                          keyField="_id"
                          onTableChange={handleTableChange}
                          noDataIndication={() => (
                             <div className="text-center py-5 text-slate-500">
                               {isLoading ? t("common.loading", "Loading....") : t("page.notificationTemplates.noTemplates", "No templates found")}
                             </div>
                           )}
                        />
                      </div>
                      <Pagination
                        totalRows={meta?.totalItems || 0}
                        rowsPerPage={tableState.limit}
                        currentPage={tableState.page - 1}
                        onPageChange={(page) =>
                          setTableState((prev) => ({ ...prev, page: page + 1 }))
                        }
                        onRowsPerPageChange={(limit) =>
                          setTableState((prev) => ({ ...prev, limit, page: 1 }))
                        }
                        t={t}
                      />
                    </>
                  )}
                </ToolkitProvider>
              </div>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default NotificationTemplateTable;